import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import {
  fetchUsersDetailed,
  transformUsersForTable,
  mergeAllModuleUsers,
  getAllMarketCodesFromRegionData,
} from "../../api/userManagementApi";
import { useReduxPersistRehydrated } from "../useReduxPersistRehydrated";

// Static fallback region/market hierarchy (used when Redux data is not available)
export const regionMarketData = {
  "North America Region": {
    markets: ["US Market", "Canada Market"],
    scopes: {
      "US Market": "Deepdive; Execution view; Market Overview",
      "Canada Market": "Deepdive; Execution view; Market Overview",
    },
  },
  "South America Region": {
    markets: ["Brazil Market", "Argentina Market"],
    scopes: {
      "Brazil Market": "Market Overview; Analysis",
      "Argentina Market": "Market Overview",
    },
  },
  "Europe Region": {
    markets: ["UK Market", "Germany Market"],
    scopes: {
      "UK Market": "Market Overview; Analysis",
      "Germany Market": "Deepdive; Market Overview",
    },
  },
  "Asia Pacific Region": {
    markets: ["Japan Market"],
    scopes: {
      "Japan Market": "Deepdive; Execution view",
    },
  },
};

/**
 * Get region-market data with Redux fallback
 * @param {Object} reduxRegionMarketData - Region-market data from Redux store
 * @returns {Object} Region-market data (Redux if available, otherwise static fallback)
 */
export const getRegionMarketDataWithFallback = (reduxRegionMarketData) => {
  if (reduxRegionMarketData && Object.keys(reduxRegionMarketData).length > 0) {
    return reduxRegionMarketData;
  }
  return regionMarketData;
};

// Calculate rowSpans for table cell merging
const calculateRowSpans = (flattenedData) => {
  const rowSpans = {};

  // Group data by user and role for proper calculation
  const userGroups = {};
  flattenedData.forEach((row) => {
    if (!userGroups[row.userId]) {
      userGroups[row.userId] = {};
    }
    if (!userGroups[row.userId][row.roleIndex]) {
      userGroups[row.userId][row.roleIndex] = [];
    }
    userGroups[row.userId][row.roleIndex].push(row);
  });

  flattenedData.forEach((row) => {
    const { userId, roleIndex, productIndex } = row;

    // Calculate user column rowSpan - only for the very first row of the user
    if (productIndex === 0 && roleIndex === 0) {
      const userRowCount = flattenedData.filter(
        (r) => r.userId === userId
      ).length;
      rowSpans[`${row.key}-name`] = userRowCount;
    }

    // Calculate role column rowSpan - only for the first product of each role
    if (productIndex === 0) {
      const roleRowCount = userGroups[userId][roleIndex].length;
      rowSpans[`${row.key}-role`] = roleRowCount;
    }

    // Product, Market, Action columns always have rowSpan 1
    rowSpans[`${row.key}-product`] = 1;
    rowSpans[`${row.key}-market`] = 1;
    rowSpans[`${row.key}-action`] = 1;
  });

  return rowSpans;
};

export const useUserData = () => {
  // The full roster (active + deactivated) for the workspace, fetched once via
  // status=all. `users` is the CURRENT view's slice of it, derived below and
  // consumed/mutated by the editing + action hooks. Keeping both lets the tabs
  // carry live counts and the search hint see the deactivated set without a
  // second fetch or a refetch on every tab switch.
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false); // Track if we've fetched data
  // Roles the signed-in caller may grant in this workspace, from the roster
  // response. `null` means not yet known (never fetched, or the fetch failed),
  // which the pickers must render differently from `[]` ("may grant nothing"):
  // one is a loading/error state, the other is a real answer.
  const [assignableRoles, setAssignableRoles] = useState(null);
  // Active/Deactivated view: a pure client-side partition of
  // `allUsers` — switching it never refetches (see the sync effect below).
  const [statusFilter, setStatusFilter] = useState("active");
  const [messageApi, contextHolder] = message.useMessage();

  // Get user from Redux auth state
  const user = useSelector((state) => state.auth?.user);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const isInitialized = useSelector((state) => state.auth?.isInitialized);
  const userData = useSelector((state) => state.auth?.userData);
  const userDataFetchedThisSession = useSelector((state) => state.auth?.userDataFetchedThisSession);
  const isRehydrated = useReduxPersistRehydrated();

  // Get selectedWorkspace from Redux
  const selectedWorkspace = useSelector(
    (state) => state.auth?.selectedWorkspace
  );
  // Resolved current-workspace key (staff-aware via ensureImplicitWorkspaceEntry).
  // Passed to the roster fetch so a grant-less staffer's workspace resolves even
  // though their freshly-fetched profile carries no id_client membership entry.
  const authIdClient = useSelector((state) => state.auth?.id_client);

  // Get region-market data from Redux with fallback
  const reduxRegionMarketData = useSelector(
    (state) => state.workspace?.regionMarketData
  );
  const regionMarketData = getRegionMarketDataWithFallback(
    reduxRegionMarketData
  );

  // Helper function to initialize market selections
  const initializeMarketSelections = (usersData) => {
    const initialSelectedMarkets = {};
    usersData.forEach((user) => {
      if (user.market) {
        initialSelectedMarkets[user.key] = Array.isArray(user.market)
          ? user.market
          : [user.market];
      }
    });
    return initialSelectedMarkets;
  };

  const checkAdminAndLoadUsers = async (selectedWorkspace = null) => {
    try {
      // const isAdmin = await isCurrentUserAdmin();
      // if (!isAdmin) {
      //   messageApi.error("You don't have permission to access user management.");
      //   return;
      // }
      loadUsers(selectedWorkspace);
    } catch (error) {
      console.error("Error checking admin status:", error);
      messageApi.error("Error checking permissions.");
      // Load users anyway for development/testing
      loadUsers(selectedWorkspace);
    }
  };

  const loadUsers = async (selectedWorkspace = null) => {
    setLoading(true);
    try {
      // Fetch the WHOLE roster (active + deactivated) in one call so the tabs
      // can show live counts and the search hint can see the deactivated set
      // without a second request. A fresh profile (null) is used so a stale
      // persisted account_type can't misclassify the caller; the workspace is
      // resolved from the caller's active selection via authIdClient (see
      // extractClientId — staff-aware). The active/deactivated split is a
      // client-side partition of this result (see the sync effect).
      const apiResponse = await fetchUsersDetailed(
        null,
        selectedWorkspace,
        "all",
        authIdClient
      );

      // Transform the API response, then merge rows for users with all modules + all markets
      const transformedData = transformUsersForTable(apiResponse);
      const allAvailableCodes = getAllMarketCodesFromRegionData(reduxRegionMarketData);
      const mergedUsers = mergeAllModuleUsers(transformedData.users, allAvailableCodes);

      setAllUsers(mergedUsers);
      setAssignableRoles(apiResponse.assignableRoles ?? null);
      setHasFetchedData(true); // Mark that we've successfully fetched data
      return initializeMarketSelections(transformedData.users);
    } catch (error) {
      console.error("Error loading users from API:", error);
      messageApi.error("Failed to load users from API.");

      // Never fabricate rows. A real fetch failure (or an unresolved workspace)
      // must read as "no data", not invented accounts — a made-up user is worse
      // than none. The error toast above surfaces the failure; the table
      // renders its empty state.
      setAllUsers([]);
      // Back to "unknown", not "may grant nothing" — the ladder is unread, so
      // the pickers say so rather than blaming the caller's permissions.
      setAssignableRoles(null);
      setHasFetchedData(true);
      return initializeMarketSelections([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side partition of the full roster. A user's delete_time is global
  // (account-level), so every one of a user's flattened rows carries the same
  // isDeactivated flag — partitioning the merged list keeps each user's rows
  // together. Counts are by unique user, matching how the table paginates.
  const activeUsers = useMemo(
    () => allUsers.filter((u) => !u.isDeactivated),
    [allUsers]
  );
  const deactivatedUsers = useMemo(
    () => allUsers.filter((u) => u.isDeactivated),
    [allUsers]
  );
  const activeCount = useMemo(
    () => new Set(activeUsers.map((u) => u.userId)).size,
    [activeUsers]
  );
  const deactivatedCount = useMemo(
    () => new Set(deactivatedUsers.map((u) => u.userId)).size,
    [deactivatedUsers]
  );

  // Feed the current view's partition to `users` (what the table, editing, and
  // action hooks read/mutate). Runs on a tab switch or a reload — both of which
  // intentionally discard any unsaved in-editor rows, exactly as a refetch did.
  useEffect(() => {
    setUsers(statusFilter === "deactivated" ? deactivatedUsers : activeUsers);
  }, [activeUsers, deactivatedUsers, statusFilter]);

  // Get market scope for a market or region
  const getMarketScope = (marketOrRegionName) => {
    // Check if it's a region name
    if (regionMarketData[marketOrRegionName]) {
      // For regions, combine all unique scopes from its markets
      const regionData = regionMarketData[marketOrRegionName];
      const allScopes = new Set();

      Object.values(regionData.scopes).forEach((scopeString) => {
        scopeString.split("; ").forEach((scope) => allScopes.add(scope));
      });

      return Array.from(allScopes);
    }

    // Find the market scope from regionMarketData
    for (const [, regionData] of Object.entries(regionMarketData)) {
      if (regionData.scopes[marketOrRegionName]) {
        return regionData.scopes[marketOrRegionName].split("; ");
      }
    }
    return ["Market Overview"]; // Default scope if not found
  };

  // Helper functions for user key management
  const getBaseUserKey = (key) => key.split("-")[0];

  const getUserRowsByBaseKey = (users, baseKey) =>
    users.filter((u) => getBaseUserKey(u.key) === baseKey);

  // Process market selections to handle regions and individual markets
  const flattenMarketSelections = (selections) => {
    const flattened = [];
    selections.forEach((item) => {
      if (Object.keys(regionMarketData).includes(item)) {
        flattened.push(...regionMarketData[item].markets);
      } else {
        flattened.push(item);
      }
    });
    return flattened;
  };

  const processMarketSelections = (selections) => {
    const processed = [];
    const flattenedMarkets = flattenMarketSelections(selections);

    // Track which markets have been processed as part of regions
    const processedMarkets = new Set();

    // First, check for complete regions
    Object.entries(regionMarketData).forEach(([regionName, regionData]) => {
      const regionMarkets = regionData.markets;
      const allMarketsSelected = regionMarkets.every((market) =>
        flattenedMarkets.includes(market)
      );

      if (allMarketsSelected) {
        processed.push(regionName);
        regionMarkets.forEach((market) => processedMarkets.add(market));
      }
    });

    // Then add individual markets that aren't part of complete regions
    flattenedMarkets.forEach((market) => {
      if (!processedMarkets.has(market)) {
        processed.push(market);
      }
    });

    return processed;
  };

  // Create user rows for markets
  const createUserRowsForMarkets = (user, selections, baseKey) => {
    let keyCounter = 1;
    const processedSelections = processMarketSelections(selections);
    return processedSelections.map((marketOrRegion) => ({
      ...user,
      key: `${baseKey}-${keyCounter++}`,
      market: marketOrRegion,
      product: getMarketScope(marketOrRegion),
    }));
  };

  useEffect(() => {
    // Only load users after Firebase auth is initialized, Redux has rehydrated, user is authenticated,
    // userData has been fetched, AND the data has been fetched in this session (not just from cache)
    if (isInitialized && isRehydrated && isAuthenticated && user && userData && userDataFetchedThisSession) {
      setHasFetchedData(false); // Reset flag when the workspace changes
      checkAdminAndLoadUsers(selectedWorkspace);
    }
    // No statusFilter dep: the tab switch is a client-side partition (sync
    // effect above), not a refetch.
  }, [isInitialized, isRehydrated, isAuthenticated, user, userData, userDataFetchedThisSession, selectedWorkspace?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback: Load users after component mounts if users array is still empty and we haven't fetched data yet
  useEffect(() => {
    if (isInitialized && isRehydrated && isAuthenticated && user && userData && userDataFetchedThisSession && users.length === 0 && !loading && !hasFetchedData) {
      loadUsers(selectedWorkspace); // This will try API first, then fallback to mock data
    }
  }, [isInitialized, isRehydrated, isAuthenticated, user, userData, userDataFetchedThisSession, users.length, loading, hasFetchedData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload users using the current Redux workspace (no argument needed at call site)
  const reloadUsers = () => loadUsers(selectedWorkspace);

  return {
    users,
    setUsers,
    // Partitions + counts. `activeUsers` is consumed by Market View, which is a
    // lens on the ACTIVE roster and must ignore the User-list-only
    // active/deactivated filter; `deactivatedUsers` + counts drive the tab
    // counts and the empty-search hint.
    activeUsers,
    deactivatedUsers,
    activeCount,
    deactivatedCount,
    loading,
    hasFetchedData,
    assignableRoles,
    statusFilter,
    setStatusFilter,
    contextHolder,
    messageApi,
    regionMarketData,
    getMarketScope,
    getBaseUserKey,
    getUserRowsByBaseKey,
    flattenMarketSelections,
    processMarketSelections,
    createUserRowsForMarkets,
    initializeMarketSelections,
    loadUsers,
    reloadUsers,
    checkAdminAndLoadUsers, // Export for external components to reload with workspace
    calculateRowSpans,
  };
};
